interface ClosingProps {
  onBookingClick: () => void;
}

export default function Closing({ onBookingClick }: ClosingProps) {
  const steps = [
    {
      number: '01',
      title: '無料カウンセリング',
      description: 'お悩みやご希望をお聞きし、お口の状態を確認します',
      icon: 'ri-chat-smile-3-line',
      image: 'https://public.readdy.ai/ai/img_res/2ff96385990dfb1f4caa8581245c2a04.jpg'
    },
    {
      number: '02',
      title: '精密検査・治療計画',
      description: '3Dスキャンで歯並びを分析し、最適なプランを作成',
      icon: 'ri-scan-line',
      image: 'https://readdy.ai/api/search-image?query=A%20Japanese%20woman%20patient%20viewing%20her%203D%20dental%20scan%20simulation%20on%20a%20large%20monitor%20screen%20with%20a%20dentist%20explaining%2C%20bright%20modern%20clinic%20with%20advanced%20technology%2C%20professional%20medical%20photography%20showing%20innovation%20and%20precision&width=500&height=400&seq=step-2&orientation=landscape'
    },
    {
      number: '03',
      title: 'マウスピース作成・開始',
      description: 'あなた専用のマウスピースを作成し、治療スタート',
      icon: 'ri-box-3-line',
      image: 'https://readdy.ai/api/search-image?query=A%20Japanese%20woman%20in%20her%2030s%20receiving%20her%20custom%20clear%20aligners%20package%20from%20a%20dental%20staff%20member%2C%20bright%20clean%20clinic%20setting%2C%20professional%20medical%20photography%20showing%20the%20start%20of%20treatment%20with%20clear%20aligner%20cases&width=500&height=400&seq=step-3&orientation=landscape'
    },
    {
      number: '04',
      title: '定期チェック・完了',
      description: '1〜2ヶ月ごとに経過確認。理想の笑顔を手に入れます',
      icon: 'ri-trophy-line',
      image: 'https://readdy.ai/api/search-image?query=A%20happy%20Japanese%20woman%20in%20her%2030s%20with%20a%20beautiful%20confident%20smile%20after%20completing%20orthodontic%20treatment%2C%20celebrating%20with%20dental%20staff%2C%20bright%20modern%20clinic%2C%20professional%20medical%20photography%20showing%20success%20and%20joy&width=500&height=400&seq=step-4&orientation=landscape'
    }
  ];

  return (
    <section id="closing" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            理想の笑顔まで<br />
            <span className="text-teal-600">4つのステップ</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            シンプルで分かりやすい治療の流れ
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={step.image} 
                  alt={step.title} 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-cyan-500 opacity-20"></div>
                <div className="absolute top-4 left-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg">
                  <span className="text-teal-600 font-bold text-lg">{step.number}</span>
                </div>
              </div>
              <div className="p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                  <i className={`${step.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-3xl overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="relative h-80 lg:h-auto overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=A%20confident%20beautiful%20Japanese%20woman%20in%20her%2030s%20with%20a%20radiant%20genuine%20smile%20showing%20perfect%20white%20teeth%20after%20orthodontic%20treatment%2C%20bright%20natural%20lighting%2C%20clean%20modern%20background%20in%20soft%20teal%20and%20white%20tones%2C%20professional%20lifestyle%20photography%20showing%20happiness%20and%20transformation&width=800&height=600&seq=closing-cta&orientation=landscape" 
                alt="理想の笑顔を手に入れた女性" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-teal-600/50 to-transparent"></div>
            </div>
            
            <div className="p-8 md:p-12 lg:p-16 text-white flex flex-col justify-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                今すぐ始めて、<br />
                半年後の自分に<br />
                出会いませんか?
              </h3>
              <p className="text-xl mb-8 opacity-90 leading-relaxed">
                無料カウンセリングでは、あなたの歯並びの状態を確認し、最適な治療プランをご提案します。<br />
                <strong>無理な勧誘は一切ありません。</strong>
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">所要時間：約60分</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">費用：完全無料</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-2xl"></i>
                  <span className="text-lg">3Dシミュレーション付き</span>
                </div>
              </div>
              
              <button onClick={onBookingClick} className="bg-white text-teal-600 font-bold py-5 px-10 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl text-lg whitespace-nowrap cursor-pointer inline-block w-fit">
                今すぐ無料カウンセリングを予約
              </button>
              
              <p className="text-sm mt-6 opacity-75">
                ※ご予約後、24時間以内に担当者よりご連絡いたします
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
            「もっと早く始めればよかった」<br />
            多くの患者様がそうおっしゃいます。<br />
            <strong className="text-teal-600">あなたの理想の笑顔、今日から始めませんか?</strong>
          </p>
        </div>
      </div>
    </section>
  );
}